import { useState } from 'react';
import { PlayCircle, CheckCircle2, Send, Thermometer, Wind, Droplets, Pencil, X, Check, RotateCcw, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useNavigate } from 'react-router-dom';
import {
  useSiteVisit,
  useUpdateSiteVisit,
  useCompleteSiteVisit,
  useUpdateSiteVisitConditions,
  useReopenSiteVisit,
  type SiteVisitStatus,
  type WeatherData,
  type WeatherCondition,
} from '@/hooks/useSiteVisits';
import { useFeature } from '@/lib/features/FeatureProvider';
import { SiteVisitEmailModal } from './SiteVisitEmailModal';

const CONDITIONS_LABELS: Record<WeatherCondition, string> = {
  clear: 'Clear',
  partly_cloudy: 'Partly Cloudy',
  cloudy: 'Cloudy',
  rain: 'Rain',
  heavy_rain: 'Heavy Rain',
  snow: 'Snow',
  fog: 'Fog',
  thunderstorm: 'Thunderstorm',
  windy: 'Windy',
};

function WeatherChips({ data }: { data: WeatherData }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {data.temperature_c !== null && (
        <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded px-2 py-0.5">
          <Thermometer className="w-3 h-3" />
          {data.temperature_c}°C
        </span>
      )}
      {data.conditions && (
        <span className="inline-flex items-center gap-1 text-xs bg-sky-50 text-sky-700 border border-sky-200 rounded px-2 py-0.5">
          {CONDITIONS_LABELS[data.conditions] ?? data.conditions}
        </span>
      )}
      {data.humidity_pct !== null && (
        <span className="inline-flex items-center gap-1 text-xs bg-teal-50 text-teal-700 border border-teal-200 rounded px-2 py-0.5">
          <Droplets className="w-3 h-3" />
          {data.humidity_pct}%
        </span>
      )}
      {data.wind_kph !== null && (
        <span className="inline-flex items-center gap-1 text-xs bg-gray-50 text-gray-600 border border-gray-200 rounded px-2 py-0.5">
          <Wind className="w-3 h-3" />
          {data.wind_kph} km/h
        </span>
      )}
    </div>
  );
}

import { SiteVisitStatusBadge } from './SiteVisitStatusBadge';
import { SiteVisitPhotoGallery } from './SiteVisitPhotoGallery';
import { SiteVisitChecklistEditor } from './SiteVisitChecklistEditor';

interface Props {
  visitId: string;
}

interface StatusTransition {
  label: string;
  icon: React.ReactNode;
  action: () => void;
  isPending: boolean;
}

// R2: reusable inline-edit field for GC and Weather notes
function InlineEditField({
  label,
  value,
  placeholder,
  isEditable,
  onSave,
  isSaving,
}: {
  label: string;
  value: string | null;
  placeholder: string;
  isEditable: boolean;
  onSave: (newValue: string) => Promise<void>;
  isSaving: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const handleEdit = () => {
    setDraft(value ?? '');
    setIsEditing(true);
  };

  const handleSave = async () => {
    await onSave(draft);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setDraft('');
  };

  return (
    <div data-testid={`inline-field-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-1.5 mb-0.5">
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        {/* R6: always-visible edit icon (opacity-60 at rest, full on hover/focus) */}
        {isEditable && !isEditing && (
          <button
            aria-label={`Edit ${label}`}
            className="opacity-60 hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded p-0.5 transition-opacity"
            onClick={handleEdit}
          >
            <Pencil className="w-3 h-3 text-muted-foreground" />
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-1.5">
          <Textarea
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="text-sm min-h-[64px]"
            rows={3}
            placeholder={placeholder}
          />
          {/* Issue #75: sticky on mobile so iOS keyboard can't cover Save */}
          <div
            data-testid={`inline-field-actions-${label.toLowerCase().replace(/\s+/g, '-')}`}
            className="flex gap-1.5 sticky bottom-0 bg-background pt-2 pb-[env(safe-area-inset-bottom)] z-10"
          >
            <button
              aria-label="Save"
              className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-primary text-primary-foreground rounded hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
              onClick={handleSave}
              disabled={isSaving}
            >
              <Check className="w-3 h-3" />
              Save
            </button>
            <button
              aria-label="Cancel"
              className="inline-flex items-center gap-1 text-xs px-2 py-1 border rounded hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={handleCancel}
            >
              <X className="w-3 h-3" />
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-foreground/80 whitespace-pre-wrap break-words">
          {value || <span className="text-muted-foreground italic">{placeholder}</span>}
        </p>
      )}
    </div>
  );
}

export function SiteVisitDetailPanel({ visitId }: Props) {
  const { data, isLoading } = useSiteVisit(visitId);
  const updateVisit = useUpdateSiteVisit();
  const completeVisit = useCompleteSiteVisit();
  const updateConditions = useUpdateSiteVisitConditions();
  const reopenVisit = useReopenSiteVisit();
  const navigate = useNavigate();
  const emailEnabled = useFeature('site_visit_email_enabled');

  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [emailModalOpen, setEmailModalOpen] = useState(false);

  if (isLoading || !data) {
    return <div className="p-4 text-sm text-muted-foreground">Loading...</div>;
  }

  const { visit, photos, checklist_items } = data;
  const isEditable = visit.status === 'draft' || visit.status === 'in_progress' || visit.status === 'completed';
  const isSubmitted = visit.status === 'submitted';

  const TRANSITIONS: Partial<Record<SiteVisitStatus, StatusTransition>> = {
    draft: {
      label: 'Start Review',
      icon: <PlayCircle className="w-4 h-4 mr-1" />,
      action: () => updateVisit.mutate({ id: visitId, data: { status: 'in_progress' } }),
      isPending: updateVisit.isPending,
    },
    in_progress: {
      label: 'Mark Complete',
      icon: <CheckCircle2 className="w-4 h-4 mr-1" />,
      action: () => completeVisit.mutate(visitId),
      isPending: completeVisit.isPending,
    },
    completed: {
      // When email flag is ON: "Preview & Send" opens modal; plain "Submit" still available inside modal.
      // When flag is OFF: silent submit (existing behavior).
      label: emailEnabled ? 'Preview & Send' : 'Submit',
      icon: emailEnabled
        ? <Mail className="w-4 h-4 mr-1" />
        : <Send className="w-4 h-4 mr-1" />,
      action: emailEnabled
        ? () => setEmailModalOpen(true)
        : () => updateVisit.mutate({ id: visitId, data: { status: 'submitted' } }),
      isPending: updateVisit.isPending,
    },
  };

  const transition = TRANSITIONS[visit.status];

  const handleSaveGC = async (value: string) => {
    await updateConditions.mutateAsync({ id: visitId, data: { general_conditions: value || '' } });
  };

  const handleSaveWeather = async (value: string) => {
    await updateConditions.mutateAsync({ id: visitId, data: { weather_notes: value || '' } });
  };

  const handleReopen = async () => {
    if (!reopenReason.trim()) return;
    await reopenVisit.mutateAsync({ id: visitId, reason: reopenReason.trim() });
    setReopenDialogOpen(false);
    setReopenReason('');
  };

  return (
    <div className="border-t bg-muted/20 p-4 space-y-5">
      {/* Visit Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <SiteVisitStatusBadge status={visit.status} />
          </div>
          {visit.weather_data && (
            <WeatherChips data={visit.weather_data} />
          )}
          {visit.location_description && (
            <p className="text-xs text-muted-foreground">{visit.location_description}</p>
          )}
          {visit.notes && (
            <p className="text-sm text-foreground/80 whitespace-pre-wrap">{visit.notes}</p>
          )}
          {visit.deliverable_id && (
            <button
              className="inline-flex items-center gap-1.5 text-xs bg-violet-50 text-violet-700 border border-violet-200 rounded px-2 py-0.5 hover:bg-violet-100 transition-colors mt-1"
              onClick={() => navigate(`/projects/${visit.project_id}?section=deliverables`)}
            >
              <span className="font-medium">Fulfills:</span>
              <span>{visit.deliverable_title ?? 'Linked deliverable'}</span>
              {visit.deliverable_status && (
                <span className={`ml-1 px-1 py-0 rounded text-[10px] font-medium ${
                  visit.deliverable_status === 'delivered' ? 'bg-green-100 text-green-700' :
                  visit.deliverable_status === 'approved' ? 'bg-teal-100 text-teal-700' :
                  'bg-gray-100 text-gray-600'
                }`}>{visit.deliverable_status}</span>
              )}
            </button>
          )}
        </div>

        {transition && (
          <Button
            size="sm"
            variant="outline"
            className="flex-shrink-0"
            onClick={transition.action}
            disabled={transition.isPending}
          >
            {transition.icon}
            {transition.label}
          </Button>
        )}
      </div>

      {/* R2: General Conditions — always visible, editable at Draft/InProgress/Completed */}
      <div className="space-y-4">
        <InlineEditField
          label="General Conditions"
          value={visit.general_conditions ?? null}
          placeholder="Add general conditions…"
          isEditable={isEditable}
          onSave={handleSaveGC}
          isSaving={updateConditions.isPending}
        />

        {/* R2: Weather notes — always visible */}
        <InlineEditField
          label="Weather Notes"
          value={visit.weather_notes ?? null}
          placeholder="Add weather notes…"
          isEditable={isEditable}
          onSave={handleSaveWeather}
          isSaving={updateConditions.isPending}
        />

        {/* R2: Submitted state — show Reopen button instead of edit affordances */}
        {isSubmitted && (
          <Button
            size="sm"
            variant="outline"
            className="flex items-center gap-1.5 text-amber-700 border-amber-300 hover:bg-amber-50"
            onClick={() => setReopenDialogOpen(true)}
            data-testid="reopen-button"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reopen for correction
          </Button>
        )}
      </div>

      {/* Photo Gallery */}
      <SiteVisitPhotoGallery
        visitId={visitId}
        photos={photos}
        isEditable={isEditable}
      />

      {/* Checklist */}
      <SiteVisitChecklistEditor
        visitId={visitId}
        checklistItems={checklist_items}
        photos={photos}
        isEditable={isEditable}
      />

      {/* R2: Reopen confirm dialog */}
      <AlertDialog open={reopenDialogOpen} onOpenChange={setReopenDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reopen for correction?</AlertDialogTitle>
            <AlertDialogDescription>
              This will set the visit back to "Completed" so you can edit General Conditions and Weather.
              Please provide a brief reason for reopening.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-1 pb-2">
            <Textarea
              autoFocus
              placeholder="Reason for reopening (required)"
              value={reopenReason}
              onChange={(e) => setReopenReason(e.target.value)}
              className="text-sm"
              rows={2}
              data-testid="reopen-reason-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setReopenReason('')}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReopen}
              disabled={!reopenReason.trim() || reopenVisit.isPending}
              className="bg-amber-600 text-white hover:bg-amber-700"
            >
              Reopen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* R4: Preview & Send modal (feature-flagged) */}
      {emailEnabled && (
        <SiteVisitEmailModal
          open={emailModalOpen}
          onClose={() => setEmailModalOpen(false)}
          visitId={visitId}
          visitDate={visit.visit_date ?? ''}
          projectName={visit.project_name ?? ''}
        />
      )}
    </div>
  );
}
